/**
 * Demo component to test real-time feature flag updates.
 *
 * This component demonstrates how to use the new real-time feature flag hooks.
 * It's meant for testing and development purposes only.
 */

import React, { useState } from 'react';
import { useFeatureFlag, useFeatureFlags, usePostHog, PostHogFeatureFlag } from './index.js';

export const PostHogRealTimeDemo: React.FC = () => {
  const [logMessages, setLogMessages] = useState<string[]>([]);

  // Test individual feature flag with real-time updates
  const usbVideoSources = useFeatureFlag(PostHogFeatureFlag.USB_VIDEO_SOURCES_FEATURE, {
    enableRealtime: true
  });

  // Test multiple feature flags with real-time updates
  const outputFlags = useFeatureFlags(
    [
      PostHogFeatureFlag.RECORDING_FEATURE,
      PostHogFeatureFlag.NDI_OUTPUT_FEATURE,
      PostHogFeatureFlag.RTMP_OUTPUT_FEATURE
    ],
    {
      enableRealtime: true
    }
  );

  // Test PostHog hook with real-time methods
  const posthog = usePostHog();

  const addLogMessage = (message: string) => {
    setLogMessages((prev) => [
      ...prev.slice(-10),
      `${new Date().toLocaleTimeString()}: ${message}`
    ]);
  };

  const handleRefreshFlags = async () => {
    try {
      await posthog.refreshFlags();
      addLogMessage('Manual refresh completed');
    } catch (error) {
      addLogMessage(`Manual refresh failed: ${error}`);
    }
  };

  const handleImmediateRefresh = async () => {
    try {
      await posthog.triggerImmediateRefresh();
      addLogMessage('Immediate refresh triggered');
    } catch (error) {
      addLogMessage(`Immediate refresh failed: ${error}`);
    }
  };

  const handleToggleRealTime = () => {
    const newState = !posthog.isRealTimeEnabled();
    posthog.setRealTimeEnabled(newState);
    addLogMessage(`Real-time updates ${newState ? 'enabled' : 'disabled'}`);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '800px' }}>
      <h2>PostHog Real-Time Feature Flags Demo</h2>

      {/* Status Display */}
      <div
        style={{
          border: '1px solid #ccc',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}
      >
        <h3>Status</h3>
        <p>
          <strong>PostHog Ready:</strong> {posthog.ready ? '✅' : '❌'}
        </p>
        <p>
          <strong>Real-time Enabled:</strong> {posthog.isRealTimeEnabled() ? '✅' : '❌'}
        </p>
      </div>

      {/* Individual Flag Demo */}
      <div
        style={{
          border: '1px solid #ccc',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}
      >
        <h3>Individual Flag: "usb-video-sources-feature"</h3>
        <p>
          <strong>Enabled:</strong> {usbVideoSources.enabled ? '✅' : '❌'}
        </p>
        <p>
          <strong>Loading:</strong> {usbVideoSources.loading ? '⏳' : '✅'}
        </p>
        <p>
          <strong>Last Updated:</strong>{' '}
          {new Date(usbVideoSources.lastUpdated).toLocaleTimeString()}
        </p>
        {usbVideoSources.error && (
          <p style={{ color: 'red' }}>
            <strong>Error:</strong> {usbVideoSources.error.message}
          </p>
        )}
      </div>

      {/* Multiple Flags Demo */}
      <div
        style={{
          border: '1px solid #ccc',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px'
        }}
      >
        <h3>Multiple Flags: Output Features</h3>
        <p>
          <strong>Loading:</strong> {outputFlags.loading ? '⏳' : '✅'}
        </p>
        <p>
          <strong>Last Updated:</strong> {new Date(outputFlags.lastUpdated).toLocaleTimeString()}
        </p>
        {outputFlags.error && (
          <p style={{ color: 'red' }}>
            <strong>Error:</strong> {outputFlags.error.message}
          </p>
        )}
        <ul>
          <li>
            Recording: {outputFlags.flags[PostHogFeatureFlag.RECORDING_FEATURE] ? '✅' : '❌'}
          </li>
          <li>
            NDI Output: {outputFlags.flags[PostHogFeatureFlag.NDI_OUTPUT_FEATURE] ? '✅' : '❌'}
          </li>
          <li>
            RTMP Output: {outputFlags.flags[PostHogFeatureFlag.RTMP_OUTPUT_FEATURE] ? '✅' : '❌'}
          </li>
        </ul>
      </div>

      {/* Control Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Controls</h3>
        <button
          className='border border-solid hover:bg-base-50'
          onClick={handleRefreshFlags}
          disabled={!posthog.ready}
          style={{ marginRight: '10px', padding: '8px 16px' }}
        >
          Manual Refresh
        </button>
        <button
          className='border border-solid hover:bg-base-50'
          onClick={handleImmediateRefresh}
          disabled={!posthog.ready}
          style={{ marginRight: '10px', padding: '8px 16px' }}
        >
          Immediate Refresh
        </button>
        <button
          className='border border-solid hover:bg-base-50'
          onClick={handleToggleRealTime}
          disabled={!posthog.ready}
          style={{ padding: '8px 16px' }}
        >
          {posthog.isRealTimeEnabled() ? 'Disable' : 'Enable'} Real-time
        </button>
      </div>

      {/* Log Messages */}
      <div
        style={{
          border: '1px solid #ccc',
          padding: '15px',
          borderRadius: '5px'
        }}
      >
        <h3>Activity Log</h3>
        <div
          style={{
            height: '150px',
            overflowY: 'auto',
            fontSize: '12px'
          }}
        >
          {logMessages.length === 0 ? (
            <p style={{ color: '#666' }}>No activity yet...</p>
          ) : (
            logMessages.map((message, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                {message}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
        <p>
          <strong>Instructions:</strong>
        </p>
        <ul>
          <li>Toggle feature flags in your PostHog dashboard to see real-time updates</li>
          <li>Use the Manual/Immediate Refresh buttons to force flag updates</li>
          <li>Toggle real-time updates on/off to test the functionality</li>
          <li>Watch the "Last Updated" timestamps to see when flags change</li>
        </ul>
      </div>
    </div>
  );
};
